<template>
  <div class="container-fluid">
    <div class="row mb-5">
      <h1>{{ transfer.seq_number ?? transfer.code ?? "-" }}</h1>
    </div>

    <div class="row mb-4" v-if="transfer.internalOrder">
      <div class="col-12">
        <base-button
          type="primary"
          size="sm"
          @click="goToOrder(transfer.internalOrder)"
        >
          <i class="fas fa-calendar-week mr-1"></i>
          {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.VIEW_INTERNAL_ORDER") }}
        </base-button>
      </div>
    </div>

    <div class="row mb-5">
      <div class="col-12">
        <dl class="row" v-if="transfer.created_at">
          <dt class="col-sm-4">{{ $t("COMMON.CREATED_AT") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(transfer.created_at) }}
          </dd>
        </dl>

        <dl class="row" v-if="transfer.updated_at">
          <dt class="col-sm-4">{{ $t("COMMON.UPDATED_AT") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(transfer.updated_at) }}
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("ESTABLISHMENT_INTERNAL_INVOICES.INTERNAL_INVOICE_DATE") }}</dt>
          <dd class="col-sm-8">
            {{ transfer.transfer_date ? $formatDate(transfer.transfer_date, "LL") : "-" }}
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.STATUS") }}</dt>
          <dd class="col-sm-8">
            <establishment-internal-invoice-status-badge
              v-if="transfer.status"
              :transfer="transfer"
            />
            <span v-else>-</span>
          </dd>
        </dl>
        <dl class="row" v-if="transfer.submitted_at || transfer.submittedBy">
          <dt class="col-sm-4">{{ $t("ESTABLISHMENT_INTERNAL_INVOICES.SUBMITTED_AT") }}</dt>
          <dd class="col-sm-8">
            {{ transfer.submitted_at ? $timeZoneDateFormat(transfer.submitted_at) : "-" }}
          </dd>
        </dl>
        <dl class="row" v-if="transfer.submittedBy">
          <dt class="col-sm-4">{{ $t("ESTABLISHMENT_INTERNAL_INVOICES.SUBMITTED_BY") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="transfer.submittedBy" />
          </dd>
        </dl>
        <dl class="row" v-if="transfer.accepted_at || transfer.acceptedBy">
          <dt class="col-sm-4">{{ $t("ESTABLISHMENT_INTERNAL_INVOICES.ACCEPTED_AT") }}</dt>
          <dd class="col-sm-8">
            {{ transfer.accepted_at ? $timeZoneDateFormat(transfer.accepted_at) : "-" }}
          </dd>
        </dl>
        <dl class="row" v-if="transfer.acceptedBy">
          <dt class="col-sm-4">{{ $t("ESTABLISHMENT_INTERNAL_INVOICES.ACCEPTED_BY") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="transfer.acceptedBy" />
          </dd>
        </dl>
        <dl class="row" v-if="transfer.auto_accepted">
          <dt class="col-sm-4">
            {{ $t("ESTABLISHMENT_INTERNAL_INVOICES.ACCEPTED_BY") }}
          </dt>
          <dd class="col-sm-8">
            <span class="badge badge-success">
              {{ $t("INTERNAL_DOCUMENTS.AUTO_ACCEPTED") }}
            </span>
            <div class="text-muted small mt-1">
              {{ $t("INTERNAL_DOCUMENTS.AUTO_ACCEPTED_HELP") }}
            </div>
          </dd>
        </dl>

        <div class="row mt-3" v-if="transfer.non_reception_reason_code">
          <div class="col-12">
            <div class="alert alert-warning">
              <strong>{{ $t("INTERNAL_DOCUMENTS.NON_RECEPTION") }}</strong>
              <div class="small mt-1">
                {{ $t("INTERNAL_DOCUMENTS.NON_RECEPTION_HELP") }}
              </div>
            </div>
          </div>
        </div>
        <dl class="row" v-if="transfer.non_reception_reason_code">
          <dt class="col-sm-4">{{ $t("INTERNAL_DOCUMENTS.NON_RECEPTION_REASON") }}</dt>
          <dd class="col-sm-8">
            {{
              $t(
                `INTERNAL_DOCUMENTS.NON_RECEPTION_REASONS.${transfer.non_reception_reason_code}`
              )
            }}
            <div class="text-muted small" v-if="transfer.non_reception_reason">
              {{ transfer.non_reception_reason }}
            </div>
          </dd>
        </dl>

        <dl class="row" v-if="transfer.canceled_at">
          <dt class="col-sm-4">{{ $t("INTERNAL_DOCUMENTS.CANCELED_AT") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(transfer.canceled_at) }}
          </dd>
        </dl>
        <dl class="row" v-if="transfer.canceledBy">
          <dt class="col-sm-4">{{ $t("INTERNAL_DOCUMENTS.CANCELED_BY") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="transfer.canceledBy" />
          </dd>
        </dl>
        <dl class="row" v-if="transfer.cancellation_reason_code">
          <dt class="col-sm-4">{{ $t("INTERNAL_DOCUMENTS.CANCELLATION_REASON") }}</dt>
          <dd class="col-sm-8">
            {{
              $t(
                `INTERNAL_DOCUMENTS.CANCELLATION_REASONS.${transfer.cancellation_reason_code}`
              )
            }}
            <div class="text-muted small" v-if="transfer.cancellation_reason">
              {{ transfer.cancellation_reason }}
            </div>
          </dd>
        </dl>

        <div class="row mt-4" v-if="hasDiscrepancies">
          <div class="col-12">
            <div class="alert alert-warning">
              <strong>{{ $t("INTERNAL_DOCUMENTS.DISCREPANCIES_TITLE") }}</strong>
              <div class="small mt-1">
                {{ $t("INTERNAL_DOCUMENTS.DISCREPANCIES_HELP") }}
              </div>
            </div>
            <div class="table-responsive">
              <table class="table table-sm align-items-center">
                <thead class="thead-light">
                  <tr>
                    <th>{{ $t("COMMON.PRODUCT") }}</th>
                    <th class="text-right">{{ $t("INTERNAL_DOCUMENTS.ORDERED") }}</th>
                    <th class="text-right">{{ $t("INTERNAL_DOCUMENTS.RECEIVED") }}</th>
                    <th>{{ $t("INTERNAL_DOCUMENTS.DISCREPANCY") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(line, index) in transfer.discrepancies" :key="index">
                    <td>{{ line.product }}</td>
                    <td class="text-right">
                      {{ line.ordered_quantity === null ? "-" : line.ordered_quantity }}
                      <span class="text-muted small" v-if="line.unit">{{ line.unit }}</span>
                    </td>
                    <td class="text-right">
                      {{ line.received_quantity }}
                      <span class="text-muted small" v-if="line.unit">{{ line.unit }}</span>
                    </td>
                    <td class="text-danger">
                      {{ $t(`INTERNAL_DOCUMENTS.DISCREPANCIES.${line.discrepancy}`) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.TOTAL") }}</dt>
          <dd class="col-sm-8">
            {{ $formatCurrency(transfer?.pricing?.total ?? 0) }}
          </dd>
        </dl>

        <hr class="my-4" />

        <dl class="row">
          <dt class="col-sm-4">
            <h2>{{ $t("COMMON.SOURCE") }}</h2>
          </dt>
          <dd class="col-sm-8">
            <object-link
              v-if="transfer.sourceEstablishment"
              :object="transfer.sourceEstablishment"
            />
            <span v-else>
              {{ transfer.source_establishment_id || "-" }}
            </span>
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">
            <h2>{{ $t("COMMON.DESTINATION") }}</h2>
          </dt>
          <dd class="col-sm-8">
            <object-link
              v-if="transfer.destinationEstablishment"
              :object="transfer.destinationEstablishment"
            />
            <span v-else>
              {{ transfer.destination_establishment_id || "-" }}
            </span>
          </dd>
        </dl>

        <hr class="my-4" />

        <dl class="row" v-if="transfer.organization">
          <dt class="col-sm-4">{{ $t("COMMON.ORGANIZATION") }}</dt>
          <dd class="col-sm-8">
            <organization :organization="transfer.organization" />
          </dd>
        </dl>

        <dl
          class="row"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_SERVICECENTERS) && transfer.serviceCenter"
        >
          <dt class="col-sm-4">{{ $t("COMMON.SERVICECENTER") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="transfer.serviceCenter" />
          </dd>
        </dl>

        <dl class="row" v-if="transfer.allowedLocations">
          <dt class="col-sm-4">{{ $t("COMMON.LOCATIONS") }}</dt>
          <dd class="col-sm-8">
            <locations :locations="transfer.allowedLocations" />
          </dd>
        </dl>

        <dl class="row" v-if="transfer.excerpt">
          <dt class="col-sm-4">{{ $t("COMMON.NOTE") }}</dt>
          <dd class="col-sm-8">
            <div v-html="transfer.excerpt"></div>
          </dd>
        </dl>
      </div>
    </div>
  </div>
</template>

<script>
import EstablishmentInternalInvoiceStatusBadge from "./EstablishmentInternalInvoiceStatusBadge.vue";

export default {
  name: "establishment-internal-invoice-view-global",
  components: { EstablishmentInternalInvoiceStatusBadge },
  props: {
    transfer: {
      type: Object,
      required: true,
    },
  },
  computed: {
    /**
     * Les écarts sont figés au moment de la soumission : ce sont eux qui ont
     * décidé qu'une acceptation humaine était nécessaire, et ce sont eux que
     * l'établissement de production doit trancher.
     */
    hasDiscrepancies() {
      return Array.isArray(this.transfer.discrepancies)
        && this.transfer.discrepancies.length > 0;
    },
  },
  methods: {
    goToOrder(order) {
      this.$router.push({
        name: "View EstablishmentInternalOrder",
        params: { id: order.id },
      });
    },
  },
};
</script>
